import {Injector} from "@angular/core"
import {AsyncCacheMap} from "@app/common/helpers/async-cache-map/async-cache-map"
import {fetchThrowingErrors} from "@common/helpers/api/fetch"
import {ConfiguratorTemplateGraphByRevisionGQL, ConfiguratorTemplateGraphLatestGQL} from "@template-editor/helpers/configurator-template-graph.generated"
import {TemplateGraph, deserializeTemplateGraph} from "@cm/template-nodes"
import {catchError, from, map, type Observable} from "rxjs"
import * as Apollo from "apollo-angular"

export class TemplateRevisionGraphCache extends AsyncCacheMap<string, TemplateGraph, [number, number | null]> {
    constructor(injector: Injector) {
        const appolloClient = injector.get(Apollo.Apollo)
        const configuratorTemplateGraphLatestGql = new ConfiguratorTemplateGraphLatestGQL(appolloClient)
        const configuratorTemplateGraphByRevisionGql = new ConfiguratorTemplateGraphByRevisionGQL(appolloClient)
        super((_, [templateId, templateRevisionId]) => {
            const graph$ =
                templateRevisionId !== null
                    ? from(
                          fetchThrowingErrors(configuratorTemplateGraphByRevisionGql)({
                              templateLegacyId: templateId,
                              revisionLegacyId: templateRevisionId,
                          }),
                      ).pipe(map((result) => result.configuratorData?.revision?.graph))
                    : from(
                          fetchThrowingErrors(configuratorTemplateGraphLatestGql)({
                              templateLegacyId: templateId,
                          }),
                      ).pipe(map((result) => result.configuratorData?.latestRevision?.graph))

            return graph$.pipe(
                map((graph) => {
                    if (!graph) throw new Error(`Template has no valid graph`)
                    return deserializeTemplateGraph(graph)
                }),
                catchError((error) => {
                    const identifier =
                        templateRevisionId !== null ? `template id: ${templateId}, revision id: ${templateRevisionId}` : `template id: ${templateId}`
                    if (error instanceof Error) {
                        error.message = `Failed to load ${identifier}: ${error.message}`
                        throw error
                    } else throw new Error(`Failed to load ${identifier}: ${error}`)
                }),
            )
        })
    }

    evaluateTemplate(templateId: number): Observable<TemplateGraph> {
        return this.get(`template-${templateId}`, [templateId, null])
    }

    evaluateTemplateRevision(templateId: number, templateRevisionId: number): Observable<TemplateGraph> {
        return this.get(`revision-${templateId}-${templateRevisionId}`, [templateId, templateRevisionId])
    }
}
